import { CommonModule } from '@angular/common';
import {
    Component,
    DestroyRef,
    type ElementRef,
    effect,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { MessageModule } from '@openng/optimus-ui/message';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';

import { ClipboardService } from '@app/shared/services/clipboard.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import {
    DatabaseMigrationService,
    type DBUpgradeLogEvent,
} from '@app/theme/services/database-migration.service';

enum UPGRADE_STATUS {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    SUCCESS = 'success',
    ERROR = 'error',
}

@Component({
    selector: 'cor-database-migration-modal',
    templateUrl: './database-migration-modal.component.html',
    styleUrls: ['./database-migration-modal.component.scss'],
    imports: [CommonModule, DialogModule, ButtonModule, ProgressSpinnerModule, MessageModule],
})
export class DatabaseMigrationModalComponent {
    readonly logsContainer = viewChild<ElementRef<HTMLDivElement>>('logsContainer');

    protected readonly UPGRADE_STATUS = UPGRADE_STATUS;

    protected readonly migrationService = inject(DatabaseMigrationService);
    private readonly clipboardService = inject(ClipboardService);
    private readonly toast = inject(ToastNotificationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);

    readonly logs = signal<string[]>([]);
    protected readonly showUpgradeButton = signal(false);
    readonly upgradeStatus = signal<UPGRADE_STATUS>(UPGRADE_STATUS.PENDING);
    readonly upgradeError = signal<string | null>(null);

    constructor() {
        // Auto-scroll when logs update
        effect(() => {
            this.logs();
            if (this.logsContainer()) {
                this.logsContainer().nativeElement.scrollTo({
                    top: this.logsContainer().nativeElement.scrollHeight,
                    behavior: 'smooth',
                });
            }
        });

        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            const adminFlag = params.get('admin')?.toString().toLowerCase();
            console.log('adminFlag', adminFlag);
            this.showUpgradeButton.set(adminFlag === 'true' || adminFlag === '1');
        });
    }

    startUpgrade(): void {
        this.upgradeStatus.set(UPGRADE_STATUS.IN_PROGRESS);
        this.logs.set([]);

        this.migrationService
            .dbUpgrade()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (event: DBUpgradeLogEvent) => {
                    if (event.type === 'log') {
                        if (event.message) {
                            this.logs.update(logs => [...logs, event.message]);
                        }
                    } else if (event.type === 'completed') {
                        if (event.success) {
                            this.upgradeStatus.set(UPGRADE_STATUS.SUCCESS);
                            this.toast.showSuccess('Database upgrade completed successfully!');
                        } else {
                            this.upgradeStatus.set(UPGRADE_STATUS.ERROR);
                            this.upgradeError.set(event.error || 'Unknown error occurred');
                            this.logs.update(logs => [
                                ...logs,
                                '',
                                '-----------------------------------',
                                'Database upgrade failed:',
                                this.upgradeError(),
                            ]);
                            this.toast.showError(`Database upgrade failed: ${this.upgradeError()}`);
                        }
                    }
                },
                error: (error: Event) => {
                    console.error('Websocket error:', error);
                    let message = 'Error connecting to the DB.';
                    if (error instanceof CloseEvent && error.reason) {
                        message += ` Reason: ${error.reason}`;
                    } else if (error.type) {
                        message += ` Reason: ${error.type}`;
                    }
                    this.toast.showError(message);
                    this.upgradeStatus.set(UPGRADE_STATUS.ERROR);
                },
            });
    }

    onClose(): void {
        if (this.upgradeStatus() !== UPGRADE_STATUS.IN_PROGRESS) {
            this.migrationService.visible.set(false);
        }
    }

    continueToPlatform(): void {
        this.migrationService.visible.set(false);
        window.location.reload();
    }

    copyLogs(): void {
        const logsText = this.logs().join('\n');
        if (!logsText.trim()) {
            this.toast.showInfo('No logs to copy.');
            return;
        }

        this.clipboardService.copyToClipboard(logsText).then(
            () => this.toast.showSuccess('Logs copied to clipboard.'),
            () => this.toast.showError('Failed to copy logs to clipboard.'),
        );
    }
}
